/**
 * `@captello/ulc-webview-sdk/embedded-app` — for the **Captello mobile app** (or any
 * host playing its role) that embeds an application in an iframe and serves it native
 * services over `postMessage`.
 *
 * {@link EmbeddedAppClient} wraps the iframe: it delivers the embedded application's
 * requests to typed handlers and posts the app's responses back. The embedded
 * application's own side is `MobileHostClient` (`@captello/ulc-webview-sdk/mobile-host`).
 *
 * Not to be confused with `CaptelloWebview`, which wraps an iframe of the *capture
 * webview* — a different protocol (JSON strings, snake_case types).
 *
 * @example
 * import { EmbeddedAppClient, MobileHostRequestType } from "@captello/ulc-webview-sdk/embedded-app";
 *
 * const embedded = new EmbeddedAppClient(iframe, { targetOrigin: new URL(iframe.src).origin });
 * embedded.onRequest(MobileHostRequestType.RequestAuthToken, async () => {
 *     embedded.sendAuthToken(await mintMagicToken());
 * });
 * embedded.onRequest(MobileHostRequestType.OpenScanner, async () => {
 *     for (const person of await scan()) embedded.sendScannerResult(person);
 *     embedded.sendScannerClosed();
 * });
 */

import type { FrameLike, Unsubscribe } from "./client";
import {
    makeSubscription,
    MobileHostRequestType,
    MobileHostResponseType,
    parseMobileHostRequest,
} from "./mobile-app-protocol";
import type { MobileHostRequest, MobileHostRequestMap, MobileHostResponse, ScannedPerson } from "./mobile-app-protocol";

export {
    MobileHostRequestType,
    MobileHostResponseType,
    parseMobileHostRequest,
    parseMobileHostResponse,
} from "./mobile-app-protocol";
export type {
    MobileHostRequest,
    MobileHostRequestMap,
    MobileHostResponse,
    MobileHostResponseMap,
    AuthTokenMessage,
    ScannerResultMessage,
    ScannerClosedMessage,
    ScannedPerson,
    MeetingScheduledRequest,
} from "./mobile-app-protocol";
export type { FrameLike } from "./client";

/** Listener for a specific request type from the embedded application. */
export type MobileHostRequestListener<T extends MobileHostRequestType> = (request: MobileHostRequestMap[T]) => void;

/** Listener for every request from the embedded application (used by {@link EmbeddedAppClient.onAnyRequest}). */
export type AnyMobileHostRequestListener = (request: MobileHostRequest) => void;

export interface EmbeddedAppClientOptions {
    /**
     * Origin to validate incoming requests against and to target outgoing responses.
     * A full URL is accepted and reduced to its origin.
     *
     * Defaults to the iframe's `src` origin, read fresh on every send so a `src` bound
     * after construction still works. When neither is available the client refuses to
     * send rather than fall back to `"*"` — a response can carry an auth token, and
     * `"*"` would hand it to whatever the frame navigated to.
     */
    targetOrigin?: string;
    /** Window to listen on. Defaults to the global `window`. */
    hostWindow?: Window;
    /**
     * Only accept requests whose `event.source` is the bound iframe's window. Defaults
     * to `true`. Set `false` if the application relays through another window.
     */
    matchSource?: boolean;
}

function toOrigin(value: string | undefined): string | undefined {
    if (!value) return undefined;
    if (value === "*") return value;
    try {
        return new URL(value).origin;
    } catch {
        return undefined;
    }
}

/**
 * Mobile-app-side client for an application embedded in an iframe.
 *
 * Attaches a single `message` listener on construction; call {@link destroy} when the
 * iframe goes away.
 */
export class EmbeddedAppClient {
    private readonly frame: FrameLike;
    private readonly hostWindow: Window;
    private readonly explicitOrigin: string | undefined;
    private readonly matchSource: boolean;
    private readonly listeners = new Map<
        MobileHostRequestType,
        Set<MobileHostRequestListener<MobileHostRequestType>>
    >();
    private readonly anyListeners = new Set<AnyMobileHostRequestListener>();
    private readonly boundHandler = (event: MessageEvent) => this.handleMessage(event);
    private destroyed = false;

    constructor(frame: FrameLike, options: EmbeddedAppClientOptions = {}) {
        if (!frame) {
            throw new Error("EmbeddedAppClient: a mounted iframe element (or { contentWindow }) is required.");
        }
        const hostWindow = options.hostWindow ?? (typeof window !== "undefined" ? window : undefined);
        if (!hostWindow) {
            throw new Error(
                "EmbeddedAppClient: no host window available. Pass `hostWindow` when constructing outside a browser.",
            );
        }
        this.frame = frame;
        this.hostWindow = hostWindow;
        this.explicitOrigin = toOrigin(options.targetOrigin);
        this.matchSource = options.matchSource ?? true;
        this.hostWindow.addEventListener("message", this.boundHandler);
    }

    /** Subscribe to one request type. Returns a handle with `unsubscribe()` (also callable). */
    onRequest<T extends MobileHostRequestType>(type: T, listener: MobileHostRequestListener<T>): Unsubscribe {
        let set = this.listeners.get(type);
        if (!set) {
            set = new Set();
            this.listeners.set(type, set);
        }
        set.add(listener as MobileHostRequestListener<MobileHostRequestType>);
        return makeSubscription(() => {
            set?.delete(listener as MobileHostRequestListener<MobileHostRequestType>);
        });
    }

    /** Subscribe to every request. */
    onAnyRequest(listener: AnyMobileHostRequestListener): Unsubscribe {
        this.anyListeners.add(listener);
        return makeSubscription(() => {
            this.anyListeners.delete(listener);
        });
    }

    /**
     * Post a response into the embedded application. Returns `false` when nothing was
     * sent: the client is destroyed, the iframe has no window, or no target origin could
     * be resolved (see {@link EmbeddedAppClientOptions.targetOrigin}).
     */
    send(response: MobileHostResponse): boolean {
        if (this.destroyed) return false;
        const target = this.frame.contentWindow;
        const origin = this.resolveOrigin();
        if (!target || !origin) return false;
        target.postMessage(response, origin);
        return true;
    }

    /** Answer a {@link MobileHostRequestType.RequestAuthToken} request. */
    sendAuthToken(token: string): boolean {
        return this.send({ type: MobileHostResponseType.AuthToken, token });
    }

    /** Post one scanned person of the current scanner session. */
    sendScannerResult(person: ScannedPerson): boolean {
        return this.send({
            type: MobileHostResponseType.ScannerResult,
            badgeId: person.badgeId,
            result: person.fields,
        });
    }

    /** Fail the current scanner session with a display-ready message. */
    sendScannerError(message: string): boolean {
        return this.send({ type: MobileHostResponseType.ScannerResult, message });
    }

    /** End the current scanner session; send after the last result, or on cancel. */
    sendScannerClosed(): boolean {
        return this.send({ type: MobileHostResponseType.ScannerClosed });
    }

    /** Remove the `message` listener and drop every subscription. Safe to call more than once. */
    destroy(): void {
        if (this.destroyed) return;
        this.destroyed = true;
        this.hostWindow.removeEventListener("message", this.boundHandler);
        this.listeners.clear();
        this.anyListeners.clear();
    }

    private resolveOrigin(): string | undefined {
        if (this.explicitOrigin) return this.explicitOrigin;
        const src = "src" in this.frame ? (this.frame as { src?: string }).src : undefined;
        return toOrigin(src);
    }

    private handleMessage(event: MessageEvent): void {
        if (this.matchSource && event.source !== this.frame.contentWindow) return;
        const origin = this.resolveOrigin();
        if (origin && origin !== "*" && event.origin !== origin) return;
        const request = parseMobileHostRequest(event.data);
        if (!request) return;
        for (const listener of Array.from(this.anyListeners)) {
            listener(request);
        }
        const set = this.listeners.get(request.type);
        if (!set) return;
        for (const listener of Array.from(set)) {
            listener(request);
        }
    }
}
